import { Controller } from "@hotwired/stimulus"

// The Review Activity panel's own behaviour: search, status filter, collapse, and
// the inline reply composer on an expanded row.
//
// Deep-linking to the commented element is NOT handled here — the row's anchor
// carries the `course-builder#refresh` params, exactly like the before-you-submit
// blockers, so the existing navigation path stays the single implementation.
export default class extends Controller {
  static targets = [
    "row", "list", "search", "searchField", "searchToggle", "collapseToggle",
    "filterOption", "thread", "replyField", "noMatch", "loading", "error", "replyTemplate"
  ]
  // `context` carries course_id / course_version_id / course_snapshot_id: the
  // comments endpoint resolves the snapshot under review from them, and without
  // them the reviewer authorization check rejects the write.
  static values = { commentsUrl: String, context: Object }

  connect() {
    this.activeFilter = "all"
  }

  toggleSearch() {
    const expanded = this.searchTarget.hidden
    this.searchTarget.hidden = !expanded
    this.searchToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")

    if (expanded) {
      this.searchInput()?.focus()
    } else {
      if (this.searchInput()) this.searchInput().value = ""
      this.applyFilters()
    }
  }

  toggleCollapse() {
    const collapsed = this.element.classList.toggle("is-collapsed")
    this.collapseToggleTarget.setAttribute("aria-expanded", collapsed ? "false" : "true")
  }

  selectFilter(event) {
    this.activeFilter = event.currentTarget.dataset.filterKey || "all"

    this.filterOptionTargets.forEach((option) => {
      option.classList.toggle("is-active", option === event.currentTarget)
    })

    this.applyFilters()
  }

  filter() {
    this.applyFilters()
  }

  applyFilters() {
    const term = (this.searchInput()?.value || "").trim().toLowerCase()
    let visibleCount = 0

    this.rowTargets.forEach((row) => {
      const matchesTerm = !term || (row.dataset.searchText || "").includes(term)
      const matchesFilter = this.activeFilter === "all" || row.dataset.commentStatus === this.activeFilter
      const visible = matchesTerm && matchesFilter

      row.hidden = !visible
      if (visible) visibleCount += 1
    })

    if (this.hasNoMatchTarget) this.noMatchTarget.hidden = visibleCount > 0
  }

  // The kebab and the row label both open the thread; the label additionally
  // navigates, which is why they are separate actions on the same row.
  toggleThread(event) {
    const thread = event.currentTarget.closest(".course-review-comment")?.querySelector("[data-course-review-activity-target='thread']")
    if (!thread) return

    thread.hidden = !thread.hidden
    if (!thread.hidden) thread.querySelector("input")?.focus()
  }

  openThread(event) {
    const thread = event.currentTarget.closest(".course-review-comment")?.querySelector("[data-course-review-activity-target='thread']")
    if (thread) thread.hidden = false
  }

  async submitReply(event) {
    if (event.key !== "Enter" || event.shiftKey) return

    event.preventDefault()

    const field = event.currentTarget
    const body = field.value.trim()
    if (!body || !this.hasCommentsUrlValue) return

    field.disabled = true
    this.hideError()

    try {
      const response = await fetch(this.commentsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          ...this.contextValue,
          comment: {
            parent_id: field.dataset.parentId,
            lesson_content_id: field.dataset.lessonContentId,
            section: field.dataset.section || null,
            child_key: field.dataset.childKey || null,
            body
          }
        })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) throw new Error("reply failed")

      this.appendReply(field, payload.comment)
      field.value = ""
    } catch (_error) {
      this.showError()
    } finally {
      field.disabled = false
      field.focus()
    }
  }

  // Markup comes from the server-rendered <template>, never from string building.
  appendReply(field, comment) {
    const thread = field.closest("[data-course-review-activity-target='thread']")
    if (!thread || !this.hasReplyTemplateTarget) return

    let replies = thread.querySelector(".course-review-comment__replies")
    if (!replies) {
      replies = document.createElement("ul")
      replies.className = "course-review-comment__replies"
      thread.prepend(replies)
    }

    const reply = this.replyTemplateTarget.content.firstElementChild.cloneNode(true)
    reply.querySelector(".course-review-comment__reply-author").textContent = comment.author_name
    reply.querySelector(".course-review-comment__reply-body").textContent = comment.body
    replies.appendChild(reply)
  }

  // ---- Row actions ----------------------------------------------------------

  resolveComment(event) {
    this.setStatus(event, "resolved")
  }

  dismissComment(event) {
    this.setStatus(event, "dismissed")
  }

  reopenComment(event) {
    this.setStatus(event, "pending")
  }

  setStatus(event, status) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), {
      method: "PATCH",
      comment: { status }
    })
  }

  deleteComment(event) {
    event.preventDefault()

    this.changeComment(event.currentTarget.closest(".course-review-comment"), { method: "DELETE" })
  }

  // The row, the modal summary and the element's avatar are all server-rendered, so
  // a successful change asks for a re-render instead of editing any of them here —
  // the same event the composer fires, handled by course-review-sync.
  async changeComment(row, { method, comment = null }) {
    if (!row || !this.hasCommentsUrlValue) return

    this.hideError()

    try {
      const response = await fetch(`${this.commentsUrlValue}/${row.dataset.commentId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ...this.contextValue, ...(comment ? { comment } : {}) })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) throw new Error("comment change failed")

      window.dispatchEvent(new CustomEvent("course-review:changed"))
    } catch (_error) {
      this.showError()
    }
  }

  searchInput() {
    return this.hasSearchFieldTarget ? this.searchFieldTarget : null
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = false
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
